// ============================================
// ChatWindow Component
// Floating chat window (Facebook Messenger style)
// ============================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import StaffAnonymousChatWindow from './StaffAnonymousChatWindow';
import { Link, useNavigate } from 'react-router-dom';
import { useChatMessages, useTypingIndicator, useUserPresence } from '@/hooks/useChat';
import { useChatContext } from '@/context/ChatContext';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  X,
  ChevronDown,
  MoreVertical,
  Edit3,
  Info,
  Image as ImageIcon,
  FileText,
  Smile,
  Send,
  Download,
  Maximize2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAvatarInitials } from '@/lib/utils';
import type { ChatWindowState, SendMessageParams } from '@/types/chat';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { MessageInput } from './MessageInput';
import { GroupInfoModal } from './GroupInfoModal';
import { supabase } from '@/lib/supabase/client';
import { getAttachmentSignedUrl, updateUserPresence } from '@/lib/supabase/chat';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useScrollLock } from '@/hooks/useScrollLock';

// Hook para obtener signed URLs de adjuntos expirados o cuando se requiera refrescar
const useAttachmentUrls = () => {
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});

  const fetchSignedUrl = useCallback(async (path: string, messageId: string) => {
    try {
      const url = await getAttachmentSignedUrl(path);
      setAttachmentUrls((prev) => ({ ...prev, [messageId]: url }));
    } catch (e) {
      // Manejar error si es necesario
    }
  }, []);

  return { attachmentUrls, fetchSignedUrl };
};

interface ChatWindowProps {
  window: ChatWindowState;
  index: number; // Position index (0 = rightmost, 1 = second from right, etc.)
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ window, index }) => {
  // Si es un chat anónimo, renderizar el componente especial para staff
  if (window.isAnonymous && window.anonymousSessionId) {
    return <StaffAnonymousChatWindow window={window as any} index={index} />;
  }
  const navigate = useNavigate();
  const { closeChatWindow, minimizeChatWindow, maximizeChatWindow } = useChatContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const previousScrollHeight = useRef<number>(0);
  const isInitialLoad = useRef(true);
  const [isClosing, setIsClosing] = useState(false);
  const [isMinimizing, setIsMinimizing] = useState(false);
  const [observerReady, setObserverReady] = useState(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [isGroupInfoModalOpen, setIsGroupInfoModalOpen] = useState(false);
  const [groupMeta, setGroupMeta] = useState<{ name?: string; avatar_url?: string | null; description?: string | null } | null>(null);
  const hasUserScrolled = useRef(false);
  const previousMessageCountRef = useRef(0);
  const effectiveGroupId = window.groupId || (window.type === 'group' && window.id.startsWith('group-') ? window.id.slice(6) : undefined);

  const getScrollViewport = useCallback(() => {
    return scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
  }, []);

  const isNearBottom = useCallback(() => {
    const viewport = getScrollViewport();
    if (!viewport) return true;
    const threshold = 88;
    const distanceToBottom = viewport.scrollHeight - (viewport.scrollTop + viewport.clientHeight);
    return distanceToBottom <= threshold;
  }, [getScrollViewport]);

  // Get current user ID
  useEffect(() => {
    let mounted = true;
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (mounted && user) setCurrentUserId(user.id);
    };
    getCurrentUser();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const getStoreIdFromGroupDescription = (description?: string | null): string | null => {
      if (!description) return null;
      const match = description.trim().match(/^store-group:([0-9a-fA-F-]{36})$/);
      return match?.[1] || null;
    };

    const loadGroupMeta = async () => {
      if (window.type !== 'group' || !effectiveGroupId) {
        setGroupMeta(null);
        return;
      }

      const { data: rows, error } = await supabase
        .from('group_chats')
        .select('name, avatar_url, description')
        .eq('id', effectiveGroupId)
        .limit(1);

      const data = rows?.[0];
      if (error || cancelled || !data) return;

      let resolvedAvatarUrl: string | null = data.avatar_url;
      const supportStoreId = getStoreIdFromGroupDescription(data.description);

      // Para grupos de tienda: solo usar avatar de tienda; si no tiene, forzar null para mostrar siglas.
      if (supportStoreId) {
        const { data: storeData } = await supabase
          .from('stores')
          .select('avatar_url')
          .eq('id', supportStoreId)
          .maybeSingle();
        resolvedAvatarUrl = storeData?.avatar_url || null;
      }

      setGroupMeta({
        name: data.name,
        avatar_url: resolvedAvatarUrl,
        description: data.description,
      });
    };

    void loadGroupMeta();

    return () => {
      cancelled = true;
    };
  }, [window.type, effectiveGroupId]);

  const resolvedGroupTitle = window.type === 'group' ? (groupMeta?.name || window.title) : window.title;
  const resolvedGroupAvatarUrl = window.type === 'group'
    ? (groupMeta ? groupMeta.avatar_url : window.avatarUrl)
    : window.avatarUrl;

  const { messages, readByMessageId, loading, hasMore, loadingMore, loadMore, sendMessage, markAsRead } = useChatMessages({
    conversationId: window.conversationId,
    groupId: effectiveGroupId,
    anonymousSessionId: window.isAnonymous ? window.anonymousSessionId : undefined,
  });

  const { typingUsers, setTyping } = useTypingIndicator({
    conversationId: window.conversationId,
    groupId: effectiveGroupId,
  });

  const peerUserId = window.type === 'dm' ? window.otherUserId : undefined;
  const isDmWindowActive = window.type === 'dm' && Boolean(peerUserId) && window.isOpen && !window.isMinimized;
  const { isOnline, refetch: refetchPeerPresence } = useUserPresence(peerUserId ? [peerUserId] : []);

  useEffect(() => {
    if (!isDmWindowActive || !peerUserId) return;

    void refetchPeerPresence();
    void updateUserPresence('online');

    const refreshInterval = setInterval(() => {
      void refetchPeerPresence();
    }, 15000);

    const presenceHeartbeat = setInterval(() => {
      void updateUserPresence('online');
    }, 30000);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(presenceHeartbeat);
    };
  }, [isDmWindowActive, peerUserId, refetchPeerPresence]);

  // Smart autoscroll (Messenger-like):
  // - if user is near bottom, keep anchored to latest
  // - if user is reading older messages, do not force jump
  useEffect(() => {
    if (isInitialLoad.current || messages.length === 0) return;

    const previousCount = previousMessageCountRef.current;
    const hasNewMessages = messages.length > previousCount;
    const incomingCount = Math.max(0, messages.length - previousCount);
    previousMessageCountRef.current = messages.length;

    if (!hasNewMessages) return;

    const lastMessage = messages[messages.length - 1] as any;
    const isOwnMessage =
      (currentUserId && lastMessage?.sender_id === currentUserId) ||
      (!currentUserId && window.isAnonymous && lastMessage?.sender_type === 'anonymous_user');

    if (isOwnMessage || isNearBottom()) {
      setShowJumpToLatest(false);
      setNewMessagesCount(0);
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
      return;
    }

    setNewMessagesCount((prev) => prev + incomingCount);
    setShowJumpToLatest(true);
  }, [messages, currentUserId, window.isAnonymous, isNearBottom]);

  // Reset scroll state when window changes
  useEffect(() => {
    isInitialLoad.current = true;
    setHasScrolledToBottom(false);
    setShouldScrollToBottom(true);
    setObserverReady(false);
    setShowJumpToLatest(false);
    setNewMessagesCount(0);
    hasUserScrolled.current = false;
    previousMessageCountRef.current = 0;
  }, [window.id]);

  // Scroll al fondo SOLO en la primera apertura y de forma inmediata sin delays
  useEffect(() => {
    if (messages.length > 0 && isInitialLoad.current) {
      // Usar requestAnimationFrame para asegurar que el DOM esté listo
      requestAnimationFrame(() => {
        const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
          setHasScrolledToBottom(true);
          previousMessageCountRef.current = messages.length;
          isInitialLoad.current = false;
          setObserverReady(true);
        }
      });
    }
  }, [messages.length]);

  // Detectar cuando el usuario hace scroll manualmente hacia arriba
  useEffect(() => {
    const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
    if (!scrollContainer) return;

    let lastScrollTop = scrollContainer.scrollTop;

    const handleScroll = () => {
      // Solo marcar como scrolleado por usuario si scrollea hacia arriba
      if (!isInitialLoad.current && scrollContainer.scrollTop < lastScrollTop) {
        hasUserScrolled.current = true;
      }

      if (isNearBottom()) {
        setShowJumpToLatest(false);
        setNewMessagesCount(0);
      }

      lastScrollTop = scrollContainer.scrollTop;
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [isNearBottom]);

  // Mark messages as read when window is opened
  useEffect(() => {
    if (!window.isMinimized) {
      markAsRead();
    }
  }, [window.isMinimized, messages.length]);

  // Handlers con animación
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      closeChatWindow(window.id);
    }, 200);
  };

  const handleMinimize = () => {
    setIsMinimizing(true);
    setTimeout(() => {
      minimizeChatWindow(window.id);
      setIsMinimizing(false);
    }, 200);
  };

  // Automatic lazy loading when scrolling to top (solo si el usuario ha scrolleado manualmente)
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore || loadingMore || !hasScrolledToBottom || !observerReady || !hasUserScrolled.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && hasUserScrolled.current) {
          const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
          if (scrollContainer) {
            previousScrollHeight.current = scrollContainer.scrollHeight;
          }
          setShouldScrollToBottom(false);
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loadMore, hasScrolledToBottom, observerReady]);

  // Restore scroll position after loading old messages (solo si no es carga inicial)
  useEffect(() => {
    if (!shouldScrollToBottom && previousScrollHeight.current > 0 && !isInitialLoad.current) {
      const scrollContainer = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      if (scrollContainer) {
        const newScrollHeight = scrollContainer.scrollHeight;
        const scrollDiff = newScrollHeight - previousScrollHeight.current;
        scrollContainer.scrollTop = scrollDiff;
        previousScrollHeight.current = 0;
      }
    }
  }, [messages.length, shouldScrollToBottom]);

  // Reset shouldScrollToBottom when sending new messages
  const handleSendMessage = useCallback(async (params: SendMessageParams) => {
    setShouldScrollToBottom(true);
    await sendMessage(params);
  }, [sendMessage]);

  // Position calculations for stacked windows
  const { sidebarOpen } = useChatContext();
  const isMobile = typeof globalThis.window !== 'undefined' && globalThis.window.innerWidth < 1024;
  const [mobileNavbarHeight, setMobileNavbarHeight] = useState<number>(0);

  useEffect(() => {
    const updateNavbarHeight = () => {
      try {
        const el = document.querySelector('.app-bottom-nav') as HTMLElement | null;
        const h = el ? el.getBoundingClientRect().height : 0;
        setMobileNavbarHeight(h);
      } catch {
        setMobileNavbarHeight(0);
      }
    };
    updateNavbarHeight();
    if (typeof globalThis !== 'undefined' && (globalThis as any).addEventListener) {
      if ('ResizeObserver' in globalThis) {
        const nav = document.querySelector('.app-bottom-nav');
        if (nav) {
          const observer = new ResizeObserver(updateNavbarHeight);
          observer.observe(nav);
          return () => observer.disconnect();
        }
      }
      (globalThis as any).addEventListener('resize', updateNavbarHeight);
      return () => (globalThis as any).removeEventListener('resize', updateNavbarHeight);
    }
    return () => {};
  }, []);

  // Activar pull-to-close (swipe right) en móvil
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: isMobile && !window.isMinimized,
  });
  
  // Desktop: chat sidebar is ~384px (md:w-96), chat button space when closed is ~88px (56px + 32px margin)
  // If the product content sidebar is open, reserve its width (700px on desktop) so chat windows appear to its left (a su costado)
  const { productSidebarOpen, minimizedNotes, minimizedProducts } = useContentSidebar();
  const chatSidebarWidth = sidebarOpen ? 384 : 88; // distance reserved for chat sidebar/button
  const productSidebarWidth = productSidebarOpen && !isMobile ? 700 : 0; // product sidebar uses sm:w-[700px]
  
  // Count minimized items on desktop (they stack vertically, but we need space for them)
  // Each minimized note/product is w-80 (320px) on desktop displayed at bottom
  const minimizedNoteCount = !isMobile ? Object.values(minimizedNotes).filter(Boolean).length : 0;
  const minimizedProductCount = !isMobile ? Object.values(minimizedProducts).filter(Boolean).length : 0;
  const minimizedStackWidth = (minimizedNoteCount + minimizedProductCount) * 320; // w-80 = 320px each
  
  // On mobile, count total minimized items for stacking chats on top of them
  const mobileMinimizedCount = isMobile ? Object.values(minimizedNotes).filter(Boolean).length + Object.values(minimizedProducts).filter(Boolean).length : 0;
  
  // Desktop: stack horizontally (rightOffset changes), Mobile: stack vertically (bottomOffset changes)
  const rightOffset = isMobile ? 0 : chatSidebarWidth + productSidebarWidth + minimizedStackWidth + index * 336 + 24; // 336px window + 24px gap (horizontal stack desktop)
  
  // Logic adjustment: If windows are maximized on mobile, only one should be visible or they should overlap perfectly.
  // Generally, mobile users only interact with one maximized window at a time.
  // On mobile minimized: stack on top of minimized notes/products (64px each) + chat stacking (60px each)
  const bottomOffset = isMobile ? (window.isMinimized ? mobileMinimizedCount * 64 + index * 60 + 4 : 0) : 0; 

  useScrollLock(isMobile && !window.isMinimized && !isClosing && !isMinimizing, false, scrollAreaRef);

  const { attachmentUrls, fetchSignedUrl } = useAttachmentUrls();

  // Obtener signed URLs de los adjuntos de mensajes
  useEffect(() => {
    messages.forEach((message) => {
      if (
        message.type === 'attachment' &&
        message.attachment_url &&
        !attachmentUrls[message.id]
      ) {
        // Extraer el path del archivo desde la URL firmada o pública
        // Si la URL ya es una signed URL, extraer el path original
        let path = '';
        try {
          const urlObj = new URL(message.attachment_url);
          // Buscar el path después de /object/sign/chat-attachments/ o /object/public/chat-attachments/
          const match = urlObj.pathname.match(/\/object\/(sign|public)\/chat-attachments\/(.*)/);
          if (match && match[2]) {
            path = decodeURIComponent(match[2]);
          }
        } catch {
          // Si no es una URL válida, usar el string tal cual
          path = message.attachment_url;
        }
        if (path) fetchSignedUrl(path, message.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const computedZIndex = isMobile && productSidebarOpen ? 10001 : 9930;
  const minimizedZIndex = 9905; // Chat minimizado debe estar bajo las sidebars principales (z-[9998-9999])


  if (window.isMinimized) {
    return (
      <div
        className={cn(
          'fixed bottom-0 w-80 bg-background border border-border/60 rounded-t-lg shadow-2xl max-md:fixed max-md:w-full max-md:left-0 max-md:right-0 max-md:rounded-none',
          'transition-all duration-200',
          // Animación de entrada desde abajo cuando aparece por primera vez
            isClosing && 'animate-out slide-out-to-bottom duration-200'
        )}
        style={{ 
          right: isMobile ? undefined : `${rightOffset}px`, 
          bottom: `${bottomOffset + (isMobile ? mobileNavbarHeight : 0)}px`,
          zIndex: minimizedZIndex,
        }}
        data-chat-window
      >
        <div
          role="button"
          onClick={() => maximizeChatWindow(window.id)}
          className="w-full p-4 flex items-center justify-between transition-colors rounded-t-lg max-md:rounded-none bg-gradient-to-r from-primary/12 to-primary/6 text-foreground border-b border-primary/20 hover:from-primary/15 hover:to-primary/8 cursor-pointer min-h-[64px]"
        >
          <div className="flex items-center gap-3">
            <Avatar className="w-8 h-8 rounded-lg">
              <AvatarImage src={resolvedGroupAvatarUrl || undefined} />
              <AvatarFallback className="rounded-lg">{getAvatarInitials(resolvedGroupTitle || '')}</AvatarFallback>
            </Avatar>
            <span className="font-medium text-foreground">{resolvedGroupTitle}</span>
            {window.type === 'dm' && window.otherUserId && isOnline(window.otherUserId) && (
              <div className="w-2 h-2 bg-green-500 rounded-full" />
            )}
          </div>
          <button
            type="button"
            className="p-1 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'fixed flex flex-col bg-card shadow-2xl border border-primary/20 overflow-hidden',
        'w-80 h-[500px] md:h-[500px] rounded-2xl',
        'max-md:fixed max-md:right-0 max-md:w-full max-md:rounded-none max-md:border-none',
        // Animación unificada con efecto de rebote en ambos desktop y mobile
        isClosing || isMinimizing
          ? 'animate-out slide-out-to-bottom duration-200'
          : 'animate-in slide-in-from-bottom duration-200'
      )}
      style={{ 
        right: isMobile ? 0 : `${rightOffset}px`,
        bottom: 0,
        top: isMobile ? 0 : 'auto',
        height: isMobile ? '100dvh' : '500px',
        zIndex: computedZIndex,
        // En iOS Safari, 100dvh se ajusta cuando sale el teclado
        // Aseguramos que el fondo sea sólido para que no se vea el feed
        backgroundColor: 'hsl(var(--background))',
      }}
      data-chat-window
    >
      {/* Safe Area Spacer for Mobile */}
      {isMobile && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-white min-h-[64px] overflow-hidden">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {window.type === 'dm' && (window.otherUserSlug || window.otherUserId) ? (
            <Link to={`/${window.otherUserSlug || window.otherUserId}`} className="shrink-0" tabIndex={-1}>
              <Avatar className="w-8 h-8 cursor-pointer hover:opacity-80 transition-opacity">
                <AvatarImage src={resolvedGroupAvatarUrl || undefined} />
                <AvatarFallback>{getAvatarInitials(resolvedGroupTitle || '')}</AvatarFallback>
              </Avatar>
            </Link>
          ) : (
            <Avatar className="w-8 h-8" tabIndex={-1}>
              <AvatarImage src={resolvedGroupAvatarUrl || undefined} />
              <AvatarFallback>{getAvatarInitials(resolvedGroupTitle || '')}</AvatarFallback>
            </Avatar>
          )}
          <div className="flex-1 min-w-0">
            {window.type === 'dm' && (window.otherUserSlug || window.otherUserId) ? (
              <Link to={`/${window.otherUserSlug || window.otherUserId}`} className="block" tabIndex={-1}>
                <p className="font-medium truncate hover:underline cursor-pointer !text-white select-none">{resolvedGroupTitle}</p>
              </Link>
            ) : (
              <p className="font-medium truncate !text-white select-none">{resolvedGroupTitle}</p>
            )}
            {window.type === 'dm' && window.otherUserId && (
              <p className="text-[10px] !text-white/80 select-none">
                {isOnline(window.otherUserId) ? 'En línea' : 'Desconectado'}
              </p>
            )}
            {typingUsers.length > 0 && (
              <p className="text-[10px] !text-white/90 italic select-none">
                {typingUsers.length === 1
                  ? `está escribiendo...`
                  : `escribiendo...`}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Usamos DIVs simples en lugar de Buttons de UI para evitar el Input Accessory Bar en iOS (el "Done" bar) */}
          <div
            role="button"
            tabIndex={-1}
            className="h-8 w-8 hidden md:flex text-white hover:bg-white/10 items-center justify-center rounded cursor-pointer transition-colors"
            onClick={() => {
              navigate('/chat', {
                state: {
                  conversationId: window.conversationId,
                  groupId: effectiveGroupId,
                },
              });
            }}
          >
            <Maximize2 className="w-4 h-4" />
          </div>
          {window.type === 'group' && effectiveGroupId && (
            <div
              role="button"
              tabIndex={-1}
              className="h-8 w-8 text-white hover:bg-white/10 flex items-center justify-center rounded cursor-pointer transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setIsGroupInfoModalOpen(true);
              }}
            >
              <Edit3 className="w-4 h-4" />
            </div>
          )}
          <div 
            role="button"
            tabIndex={-1}
            className="h-8 w-8 text-white hover:bg-white/10 flex items-center justify-center rounded cursor-pointer transition-colors" 
            onClick={handleMinimize}
          >
            <ChevronDown className="w-4 h-4" />
          </div>
          <button
            type="button"
            tabIndex={-1}
            className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
            onClick={(e) => { e.stopPropagation(); handleClose(); }}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 bg-background overflow-x-hidden" ref={scrollAreaRef}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Cargando mensajes...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground text-center">
              No hay mensajes aún.
              <br />
              <span className="text-sm">¡Envía el primero!</span>
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 py-4">
            {messages.map((message, idx) => {
              const isCurrentUser = currentUserId && message.sender_id === currentUserId;
              
              // Para chats anónimos (visitantes no logueados):
              // Yo soy el remitente si el tipo es 'anonymous_user'
              const isAnonymousSelf = !currentUserId && window.isAnonymous && (message as any).sender_type === 'anonymous_user';
              
              // El soporte es el remitente si el tipo es 'support_admin'
              const isFromSupport = (message as any).sender_type === 'support_admin';

              // Lógica de alineación final
              // Si estoy logueado como staff: mis mensajes (isCurrentUser) a la derecha.
              // Si soy visitante: mis mensajes (isAnonymousSelf) a la derecha.
              const finalIsMe = isCurrentUser || isAnonymousSelf;

              const showAvatar =
                idx === messages.length - 1 ||
                messages[idx + 1]?.sender_id !== message.sender_id;
              const showTimestamp =
                idx === 0 ||
                new Date(message.created_at).getTime() -
                  new Date(messages[idx - 1].created_at).getTime() >
                  300000; // 5 minutes
              const showSenderName = window.type === 'group' && message.type !== 'system';
              const senderLabel = finalIsMe
                ? 'Vos'
                : (message.sender_display_name || message.sender_username || 'Usuario');
              const seenByUsers = (readByMessageId[message.id] || [])
                .filter((reader) => reader.userId !== message.sender_id)
                .slice(0, 5);

              return (
                <div
                  key={message.id}
                  className={cn(
                    'flex flex-col',
                    finalIsMe ? 'items-end' : 'items-start',
                    idx >= messages.length - 2 && 'animate-in fade-in-0 slide-in-from-bottom-1 duration-200'
                  )}
                >
                  {showTimestamp && (
                    <div className="w-full text-center text-[10px] uppercase tracking-wider text-muted-foreground/70 my-4 font-medium">
                      {format(new Date(message.created_at), 'EEEE d MMMM, HH:mm', { locale: es })}
                    </div>
                  )}

                  <div
                    className={cn('flex gap-2 max-w-[85%] min-w-0', finalIsMe ? 'flex-row-reverse' : 'flex-row')}
                  >
                    {!finalIsMe && showAvatar && (
                      <Avatar className="w-8 h-8 self-end mb-1">
                        <AvatarImage src={message.sender_avatar_url || undefined} />
                        <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                          {getAvatarInitials(message.sender_display_name || message.sender_username || '')}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    {!finalIsMe && !showAvatar && <div className="w-8" />}

                    <div className="flex flex-col gap-1 items-inherit min-w-0 max-w-full">
                      {showSenderName && (
                        <div className={cn(
                          'text-[11px] font-semibold px-1 text-muted-foreground/90',
                          finalIsMe ? 'text-right' : 'text-left'
                        )}>
                          {senderLabel}
                        </div>
                      )}
                      <div
                        className={cn(
                          'rounded-xl px-4 py-2.5 shadow-sm border text-sm break-words max-w-full min-w-0 overflow-hidden',
                          finalIsMe
                            ? 'bg-primary/15 text-foreground border-primary/35 rounded-tr-sm'
                            : 'bg-card text-foreground border-border rounded-tl-sm'
                        )}
                      >
                        {message.type === 'attachment' && (attachmentUrls[message.id] || message.attachment_url) && (
                          <div className="mb-2">
                            {message.attachment_type?.startsWith('image/') ? (
                              <a
                                href={attachmentUrls[message.id] || message.attachment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block group relative"
                              >
                                <img
                                  src={attachmentUrls[message.id] || message.attachment_url}
                                  alt={message.attachment_name || 'Imagen'}
                                  className="rounded-lg max-w-full max-h-64 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-colors flex items-center justify-center">
                                  <Download className="w-6 h-6 opacity-0 group-hover:opacity-100 text-white transition-opacity" />
                                </div>
                              </a>
                            ) : message.attachment_type?.startsWith('video/') ? (
                              <video
                                src={attachmentUrls[message.id] || message.attachment_url}
                                controls
                                className="rounded-lg max-w-full max-h-64"
                              >
                                Tu navegador no soporta video.
                              </video>
                            ) : (
                              <a
                                href={attachmentUrls[message.id] || message.attachment_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex w-full max-w-full min-w-0 items-center gap-2 p-2 bg-background/10 rounded hover:bg-background/20 transition-colors group overflow-hidden"
                              >
                                <FileText className="w-5 h-5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm truncate block max-w-full">
                                    {message.attachment_name || 'Archivo adjunto'}
                                  </span>
                                  {message.attachment_size && (
                                    <span className="text-xs opacity-70 block truncate max-w-full">
                                      {(message.attachment_size / 1024).toFixed(1)} KB
                                    </span>
                                  )}
                                </div>
                                <Download className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                              </a>
                            )}
                          </div>
                        )}

                        {message.content && (
                          <p className="leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">
                            {message.content}
                          </p>
                        )}

                        {message.is_edited && (
                          <span className="text-[10px] opacity-50 mt-1 block italic">(editado)</span>
                        )}
                      </div>
                      <div className={cn(
                        "text-[10px] text-muted-foreground/70 px-1",
                        finalIsMe ? "text-right" : "text-left"
                      )}>
                        {format(new Date(message.created_at), 'HH:mm')}
                      </div>
                      {finalIsMe && seenByUsers.length > 0 && (
                        <div className="flex items-center justify-end gap-1 px-1 mt-0.5">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Visto</span>
                          <div className="flex flex-row-reverse items-center">
                            <TooltipProvider>
                              {seenByUsers.map((reader) => {
                                const readerName = reader.displayName || reader.username || 'Usuario';
                                return (
                                  <Tooltip key={`${message.id}-${reader.userId}`}>
                                    <TooltipTrigger asChild>
                                      <Avatar className="w-4 h-4 border border-background -mr-1 first:mr-0 bg-background">
                                        <AvatarImage src={reader.avatarUrl || undefined} />
                                        <AvatarFallback className="text-[8px]">
                                          {getAvatarInitials(readerName)}
                                        </AvatarFallback>
                                      </Avatar>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      {readerName}
                                    </TooltipContent>
                                  </Tooltip>
                                );
                              })}
                            </TooltipProvider>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Marcador para scroll al final */}
            <div ref={messagesEndRef} />
            {/* Observer al FINAL para no interferir con el layout inicial */}
            {hasMore && hasScrolledToBottom && observerReady && (
              <div ref={loadMoreRef} style={{ position: 'absolute', top: 0, height: 1, width: 1, opacity: 0, pointerEvents: 'none' }} />
            )}
          </div>
        )}
      </ScrollArea>

      {showJumpToLatest && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20">
          <button
            type="button"
            onClick={() => {
              setShowJumpToLatest(false);
              setNewMessagesCount(0);
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary text-primary-foreground shadow-md hover:opacity-95 transition-opacity"
          >
            {newMessagesCount > 1 ? `Ver ${newMessagesCount} mensajes nuevos` : 'Ver mensaje nuevo'}
          </button>
        </div>
      )}

      {/* Input */}
      <MessageInput
        conversationId={window.conversationId}
        groupId={effectiveGroupId}
        recipientId={window.otherUserId}
        onSend={handleSendMessage}
        onTyping={setTyping}
        autoFocus={true}
        disableAutoFocusOnMobile={false}
      />

      {window.type === 'group' && effectiveGroupId && (
        <GroupInfoModal
          isOpen={isGroupInfoModalOpen}
          onClose={() => setIsGroupInfoModalOpen(false)}
          groupId={effectiveGroupId}
          initialAvatarUrl={resolvedGroupAvatarUrl}
          onGroupUpdated={(payload) => {
            setGroupMeta((prev) => ({
              ...(prev || {}),
              name: payload.name ?? prev?.name,
              avatar_url: payload.avatarUrl ?? prev?.avatar_url,
              description: prev?.description || null,
            }));
          }}
        />
      )}
    </div>
  );
};
